import { Card, StateCard } from '@ampersandbrands/ui';
import type { HourPoint, PerformanceHourlyData } from '@/lib/usePerformanceHourly';

/**
 * The Hourly tab on the Performance page.
 *
 * Shows today as it happens, hour by hour, against the same weekday last year.
 * It is the Sweet Spot version of the QuickSight "Store Hourly Dashboard" that
 * stores use today: same measures in the same order (sales, door count,
 * transactions, conversion, average sale), same hour-of-day grid. The one
 * deliberate difference is the comparison. That dashboard measures the day
 * against a sales plan which has not been maintained since May 2026, so its
 * goal tile renders blank. Here every comparison is last year.
 *
 * Everything on this tab is part-day and says so: the running total is matched
 * against last year through the same hour, never against last year's whole
 * day, because comparing a morning against a finished day makes a good day
 * look like a bad one.
 */

// --- Formatting -------------------------------------------------------------

function money(value: number | null, cents = false): string {
  if (value === null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  }).format(value);
}

function count(value: number | null): string {
  if (value === null) return '—';
  return value.toLocaleString('en-US');
}

function pct(ratio: number | null, digits = 1): string {
  if (ratio === null) return '—';
  return `${(ratio * 100).toFixed(digits)}%`;
}

/** 0 -> 12a, 9 -> 9a, 13 -> 1p. Short enough to sit under a bar. */
function hourLabel(hour: number): string {
  const suffix = hour < 12 ? 'a' : 'p';
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h}${suffix}`;
}

/** "1pm to 2pm", for prose rather than an axis. */
function hourSpan(hour: number): string {
  const start = hourLabel(hour).replace('a', 'am').replace('p', 'pm');
  const end = hourLabel((hour + 1) % 24).replace('a', 'am').replace('p', 'pm');
  return `${start} to ${end}`;
}

/** Read a `YYYY-MM-DD` as written, with no timezone shift. */
function longDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

function shortDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

// --- Delta styling ----------------------------------------------------------

const UP = 'text-emerald-600 dark:text-emerald-400';
const DOWN = 'text-rose-600 dark:text-rose-400';
const FLAT = 'text-candy-ink-muted dark:text-candy-dark-ink-muted';

function tone(value: number | null): { cls: string; arrow: string } {
  if (value === null || value === 0) return { cls: FLAT, arrow: '·' };
  return value > 0 ? { cls: UP, arrow: '▲' } : { cls: DOWN, arrow: '▼' };
}

function HeroChip({ comp, label }: { comp: number | null; label: string }) {
  const t = tone(comp);
  const soft =
    comp === null || comp === 0
      ? 'bg-candy-cream-warm dark:bg-candy-dark-surface-warm'
      : comp > 0
        ? 'bg-emerald-50 dark:bg-emerald-950/40'
        : 'bg-rose-50 dark:bg-rose-950/40';
  return (
    <span
      className={`inline-flex items-baseline gap-1.5 rounded-pill px-3 py-1.5 text-[13px] font-semibold ${soft} ${t.cls}`}
    >
      <span aria-hidden className="text-[11px]">
        {t.arrow}
      </span>
      {comp === null ? '—' : pct(Math.abs(comp))}
      <span className="font-medium opacity-80">{label}</span>
    </span>
  );
}

function MiniChip({ value, text }: { value: number | null; text: string }) {
  const t = tone(value);
  return (
    <span className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
      <span className={`font-bold ${t.cls}`}>
        {value !== null && <span aria-hidden>{t.arrow} </span>}
        {text}
      </span>
    </span>
  );
}

function KpiCard({ label, value, chip }: { label: string; value: string; chip: React.ReactNode }) {
  return (
    <Card className="p-4">
      <div className="text-[13px] font-semibold text-candy-ink-muted dark:text-candy-dark-ink-muted">
        {label}
      </div>
      <div className="my-1.5 text-[26px] font-bold leading-none tracking-tight text-candy-ink dark:text-candy-dark-ink">
        {value}
      </div>
      <div>{chip}</div>
    </Card>
  );
}

// --- Hour chart -------------------------------------------------------------

/**
 * Sales by hour: a filled bar for this year, a lighter bar behind it for the
 * same hour last year.
 *
 * Two bars rather than a line pair because an hour is a bucket, not a point on
 * a curve, and because hours the store has not reached yet must read as absent
 * rather than as a line falling to the floor. The hour in progress is drawn
 * hatched so nobody reads a half-finished hour as a collapse.
 */
function HourChart({ hours }: { hours: HourPoint[] }) {
  const W = 680;
  const H = 190;
  const padL = 8;
  const padR = 8;
  const padT = 12;
  const padB = 26;
  const n = hours.length;
  if (n === 0) return null;

  const values: number[] = [];
  for (const h of hours) {
    if (h.ty.sales !== null) values.push(h.ty.sales);
    if (h.ly.sales !== null) values.push(h.ly.sales);
  }
  const max = (values.length ? Math.max(...values) : 0) * 1.12 || 1;

  const slot = (W - padL - padR) / n;
  const barW = Math.max(4, Math.min(22, slot * 0.34));
  const gap = Math.max(1.5, slot * 0.06);
  const plotH = H - padT - padB;
  const y = (v: number) => padT + plotH * (1 - v / max);
  const barH = (v: number) => Math.max(v > 0 ? 2 : 0, plotH * (v / max));

  // Label every hour when there is room, otherwise every other one.
  const labelEvery = slot < 34 ? 2 : 1;

  return (
    <div>
      <div className="mb-1.5 flex flex-wrap gap-4 text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-2.5 w-2.5 rounded-sm bg-candy-pink dark:bg-candy-pink-dark" /> Today
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-2.5 w-2.5 rounded-sm bg-candy-line dark:bg-candy-dark-line" /> Same weekday last year
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-2.5 w-2.5 rounded-sm bg-candy-gold/60" /> Hour in progress
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-[190px] w-full"
        role="img"
        aria-label="Sales by hour today compared with the same weekday last year"
      >
        <defs>
          <pattern id="hourInProgress" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" className="fill-candy-pink/45 dark:fill-candy-pink-dark/45" />
            <rect width="2.5" height="6" className="fill-candy-gold/80" />
          </pattern>
        </defs>

        {[0, 1, 2].map((g) => {
          const gy = padT + plotH * (g / 2);
          return (
            <line
              key={g}
              x1={padL}
              y1={gy}
              x2={W - padR}
              y2={gy}
              className="stroke-candy-line dark:stroke-candy-dark-line"
              strokeWidth={1}
            />
          );
        })}

        {hours.map((h, i) => {
          const centre = padL + slot * i + slot / 2;
          const lyX = centre - barW - gap / 2;
          const tyX = centre + gap / 2;
          const lyV = h.ly.sales ?? 0;
          const tyV = h.ty.sales ?? 0;
          return (
            <g key={h.hour}>
              {h.ly.sales !== null && (
                <rect
                  x={lyX}
                  y={y(lyV)}
                  width={barW}
                  height={barH(lyV)}
                  rx={2}
                  className="fill-candy-line dark:fill-candy-dark-line"
                />
              )}
              {h.ty.sales !== null && (
                <rect
                  x={tyX}
                  y={y(tyV)}
                  width={barW}
                  height={barH(tyV)}
                  rx={2}
                  fill={h.current ? 'url(#hourInProgress)' : undefined}
                  className={h.current ? undefined : 'fill-candy-pink dark:fill-candy-pink-dark'}
                />
              )}
              {i % labelEvery === 0 && (
                <text
                  x={centre}
                  y={H - 8}
                  textAnchor="middle"
                  className="fill-candy-ink-muted text-[10px] dark:fill-candy-dark-ink-muted"
                  style={{ fontSize: 10 }}
                >
                  {hourLabel(h.hour)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// --- Hour-by-hour list ------------------------------------------------------

/**
 * The same grid the QuickSight dashboard shows, rebuilt for a phone: one row
 * per hour, sales first, then the supporting measures. On a narrow screen the
 * three supporting measures wrap under the row rather than scrolling sideways.
 */
function HourTable({
  hours,
  lyDate,
  localHour,
}: {
  hours: HourPoint[];
  lyDate: string;
  localHour: number | null;
}) {
  const maxSales = hours.reduce((m, h) => Math.max(m, h.ty.sales ?? 0, h.ly.sales ?? 0), 0) || 1;

  return (
    <Card className="p-5">
      <h2 className="text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">Hour by hour</h2>
      <p className="mb-4 mt-0.5 text-[13px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
        Each hour today against the same hour on {shortDate(lyDate)}, the matching weekday last year.
      </p>

      <div className="grid gap-2.5">
        {hours.map((h) => {
          const t = tone(h.comp_sales);
          // An hour with no sales is one of two different things, and saying
          // the wrong one is worse than saying nothing. If the store's clock
          // has already passed the hour, the sales exist and simply have not
          // reached us; if it has not, the hour is genuinely still ahead.
          const noSales = h.ty.sales === null && !h.current;
          const alreadyPassed = localHour !== null && h.hour <= localHour;
          const emptyLabel = alreadyPassed ? 'Waiting on the register' : 'Not reached yet';
          return (
            <div
              key={h.hour}
              className={`grid grid-cols-[46px_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 rounded-md px-1.5 py-1 ${
                h.current ? 'bg-candy-gold/10 ring-1 ring-candy-gold/40' : ''
              }`}
            >
              <div className="text-[13px] font-semibold tabular-nums text-candy-ink dark:text-candy-dark-ink">
                {hourLabel(h.hour)}
              </div>

              <div className="min-w-0">
                <div className="flex h-2.5 w-full items-center gap-1 overflow-hidden rounded-pill bg-candy-cream-warm dark:bg-candy-dark-surface-warm">
                  <span
                    className={`block h-full rounded-pill ${
                      h.current
                        ? 'bg-gradient-to-r from-candy-gold to-candy-pink'
                        : 'bg-gradient-to-r from-candy-pink to-[#D6488A]'
                    }`}
                    style={{ width: `${Math.max(h.ty.sales ? 3 : 0, ((h.ty.sales ?? 0) / maxSales) * 100)}%` }}
                  />
                </div>
                <div className="mt-1 text-[11.5px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
                  {noSales ? (
                    <>
                      {emptyLabel}
                      {/* Door counts are live, so an hour can show visitors before its sales arrive. */}
                      {h.ty.traffic !== null && h.ty.traffic > 0 && <> · {count(h.ty.traffic)} in the door</>}
                    </>
                  ) : (
                    <>
                      {count(h.ty.transactions)} sales
                      {h.ty.traffic !== null && <> · {count(h.ty.traffic)} in the door</>}
                      {h.ty.conversion !== null && <> · {pct(h.ty.conversion, 0)} bought</>}
                      {h.ty.dpt !== null && <> · {money(h.ty.dpt, true)} avg</>}
                    </>
                  )}
                </div>
              </div>

              <div className="text-right">
                <div className="text-[14px] font-bold tabular-nums text-candy-ink dark:text-candy-dark-ink">
                  {money(h.ty.sales)}
                </div>
                <div className="text-[11.5px] tabular-nums">
                  {h.comp_sales !== null && (
                    <span className={`font-semibold ${t.cls}`}>
                      <span aria-hidden>{t.arrow}</span> {pct(Math.abs(h.comp_sales), 0)}{' '}
                    </span>
                  )}
                  <span className="text-candy-ink-muted dark:text-candy-dark-ink-muted">
                    vs {money(h.ly.sales)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// --- Panel ------------------------------------------------------------------

export function HourlyPanel({ data }: { data: PerformanceHourlyData }) {
  const { hours, totals } = data;

  if (hours.length === 0) {
    return (
      <StateCard
        dashed
        title="Nothing rung up yet"
        description={`No sales recorded for ${longDate(data.business_date)} so far. This refreshes every 15 minutes.`}
      />
    );
  }

  const conv = totals.conversion;
  const convPoints =
    conv.ty !== null && conv.ly_week !== null ? (conv.ty - conv.ly_week) * 100 : null;

  // The comparison is cut at the newest hour we hold sales for, not at the
  // store's clock. Saying "through 1pm" when the clock reads 2pm is the honest
  // label, and it is the same boundary both years are summed over.
  const throughHour = data.data_through_hour;
  // Before the first sale of the day there is no "through" to name, and
  // inventing one ("through the full day") would read as a finished day.
  const heroLabel =
    throughHour === null
      ? 'Net sales · today'
      : `Net sales · today through ${hourLabel((throughHour + 1) % 24)
          .replace('a', 'am')
          .replace('p', 'pm')}`;
  const behindClock =
    throughHour !== null && data.local_hour !== null && data.local_hour > throughHour;

  const lyFull = data.ly_full_day.sales;
  const shareOfLastYear =
    totals.sales.ty !== null && lyFull !== null && lyFull > 0 ? totals.sales.ty / lyFull : null;

  return (
    <div className="space-y-4">
      {/* Hero: today so far, the comp, and the hour chart. */}
      <Card className="grid grid-cols-1 items-center gap-6 p-5 md:grid-cols-2">
        <div>
          <div className="text-[12px] font-bold uppercase tracking-wider text-candy-ink-muted dark:text-candy-dark-ink-muted">
            {heroLabel}
          </div>
          <div className="my-3 text-[clamp(44px,6vw,60px)] font-extrabold leading-none tracking-tight text-candy-ink dark:text-candy-dark-ink">
            {money(totals.sales.ty)}
          </div>
          {totals.sales.ty === null ? (
            <p className="text-[14px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
              Nothing rung up yet today.
              {lyFull !== null && <> Last year this day finished at {money(lyFull)}.</>}
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2.5">
                <HeroChip
                  comp={totals.sales.comp_week}
                  label={`vs ${money(totals.sales.ly_week)} at this point last year`}
                />
              </div>
              {lyFull !== null && (
                <p className="mt-3 text-[13px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
                  Last year this day finished at {money(lyFull)}
                  {shareOfLastYear !== null && <>. You are at {pct(shareOfLastYear, 0)} of that</>}.
                </p>
              )}
            </>
          )}
          {behindClock && (
            <p className="mt-1.5 text-[12.5px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
              Sales rung up since then are still on their way over. They usually land within the hour.
            </p>
          )}
        </div>
        <div>
          <HourChart hours={hours} />
        </div>
      </Card>

      {/* KPI row: the same measures the hourly dashboard shows. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <KpiCard
          label="Transactions"
          value={count(totals.transactions.ty)}
          chip={
            <MiniChip
              value={totals.transactions.comp_week}
              text={`${
                totals.transactions.comp_week === null ? '—' : pct(Math.abs(totals.transactions.comp_week))
              } vs LY`}
            />
          }
        />
        <KpiCard
          label="Traffic"
          value={count(totals.traffic.ty)}
          chip={
            totals.traffic.ty === null ? (
              <span className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
                No door count yet
              </span>
            ) : (
              <MiniChip
                value={totals.traffic.comp_week}
                text={`${
                  totals.traffic.comp_week === null ? '—' : pct(Math.abs(totals.traffic.comp_week))
                } counted at the door`}
              />
            )
          }
        />
        <KpiCard
          label="Conversion"
          value={pct(conv.ty)}
          chip={
            conv.ty === null ? (
              <span className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
                Needs a door count
              </span>
            ) : (
              <MiniChip
                value={convPoints}
                text={`${convPoints === null ? '—' : `${Math.abs(convPoints).toFixed(1)} pt`} visits that bought`}
              />
            )
          }
        />
        <KpiCard
          label="Avg ticket"
          value={money(totals.dpt.ty, true)}
          chip={
            <MiniChip
              value={totals.dpt.comp_week}
              text={`${totals.dpt.comp_week === null ? '—' : pct(Math.abs(totals.dpt.comp_week))} per transaction`}
            />
          }
        />
        <KpiCard
          label="Best hour"
          value={data.best_hour ? hourLabel(data.best_hour.hour) : '—'}
          chip={
            <span className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
              {data.best_hour ? `${money(data.best_hour.sales)} in ${hourSpan(data.best_hour.hour)}` : 'No sales yet'}
            </span>
          }
        />
      </div>

      <HourTable hours={hours} lyDate={data.ly_date} localHour={data.local_hour} />
    </div>
  );
}
